import $ from 'jquery';

/**
 * Client-side rendering of disclaimers
 */

/**
 * @readonly
 * @enum {string}
 */
const DisclaimerCode = {
    RESTRICTED_BILLING: 'RESTRICTED_BILLING',
    RESTRICTED_MATERIAL: 'RESTRICTED_MATERIAL',
    RESTRICTED_SHIPPING: 'RESTRICTED_SHIPPING',
    SELLER_ON_VACATION: 'SELLER_ON_VACATION',
};

/**
 * @readonly
 * @enum {string}
 */
const DisclaimerType = {
    BLOCKING: 'Blocking',
    WARNING: 'Warning',
};

const Disclaimers = {
    /**
     * The rendering "engine", takes products' meta data passed in from product/data endpoint and iterates through
     * to find disclaimers and orchestrate rendering them.
     *
     * @param {Object} productsData Collection of product data Objects
     *      e.g. - { 17: { id: 17, meta: {...} }, 22: { id: 22, meta: {...} } }
     */
    render(productsData) {
        Object.entries(productsData).forEach((entry) => {
            const product = entry[1];
            const productId = product.id.value;

            this.renderDisclaimers(this.getDisclaimers(product), productId, DisclaimerType.WARNING);
            this.renderDisclaimers(this.getDisclaimerAlerts(product), productId, DisclaimerType.BLOCKING);
        });
    },

    /**
     * Evaluates product data for a given product and returns disclaimers if available
     *
     * @param {Object} data Contains the product data for a given product
     * @returns {Array.<Object>|null} An array of product warning type disclaimers if available, else null
     */
    getDisclaimers(data) {
        return data && data.meta && data.meta.disclaimers
            ? data.meta.disclaimers.filter((disclaimer) => disclaimer.type === DisclaimerType.WARNING)
            : null;
    },

    /**
     * Evaluates a product and returns disclaimers that are to be displayed as disclaimer alerts
     *
     * @param {Object} product Contains product properties
     * @returns {Array.<Object>|null} An array of product blocking type disclaimers if available, null otherwise
     */
    getDisclaimerAlerts(product) {
        return product && product.meta && product.meta.disclaimers
            ? product.meta.disclaimers.filter((disclaimer) => disclaimer.type === DisclaimerType.BLOCKING)
            : null;
    },

    /**
     * Renders disclaimer alerts
     *
     * @param {Array.<Object>|null} disclaimers An array of blocking product disclaimers
     * @param {number} productId A product ID
     * @param {string} disclaimerType The disclaimer type
     */
    renderDisclaimers(disclaimers, productId, disclaimerType) {
        const isBlocking = disclaimerType === DisclaimerType.BLOCKING;
        const containerClass = isBlocking
            ? 'js-product-disclaimers-alert-container'
            : 'js-product-disclaimers-container';
        const isPDP = $('.js-product-detail').length;
        const disclaimerClass = isBlocking ? 'product-disclaimer-alert' : 'product-disclaimer-warning';
        const $container = $(`.${containerClass}[data-id='${productId}']`);
        if ($container.length) {
            $container.empty();
            if (disclaimers && disclaimers.length) {
                disclaimers.forEach((disclaimer) => {
                    const $disclaimer = $('<li>', { class: disclaimerClass });
                    $('<div>', { class: `cicon cicon-${disclaimer.icon}` }).appendTo($disclaimer);
                    const $text = $('<p>', {
                        class: 'product-disclaimer-text',
                        text: disclaimer.display,
                    });
                    if (disclaimer.url_href && disclaimer.url_text) {
                        $text.append(' '); // adds a space after display text, if there is link info for disclaimers
                        const linkAttrs = {
                            href: disclaimer.url_href,
                            text: disclaimer.url_text,
                        };
                        if (disclaimer.code === DisclaimerCode.RESTRICTED_BILLING) {
                            linkAttrs.class = 'js-product-action-move-to-favorites';
                        } else {
                            linkAttrs.target = '_blank';
                        }

                        $('<a>', linkAttrs).appendTo($text);
                    }
                    $text.appendTo($disclaimer);

                    if (disclaimer.code === DisclaimerCode.SELLER_ON_VACATION) {
                        $disclaimer.appendTo(
                            $container.filter('[data-orderstatus!="E"][data-orderstatus!="L"][data-orderstatus!="F"]'),
                        );
                    } else {
                        $container.append($disclaimer);
                    }

                    if (isPDP && disclaimer.code === DisclaimerCode.RESTRICTED_MATERIAL) {
                        // prepend asterisks to the pertinent material name
                        const materialListSelector = '.js-detail-list.js-materials';
                        const $materials = $(`${materialListSelector} dd`);
                        $.each($materials, (_, material) => {
                            if (disclaimer.footnote_related_names.includes(material.innerHTML)) {
                                material.append('*');
                            }
                        });

                        // remove any pre-existing footnotes to avoid duplicates
                        $('.js-product-disclaimer-footnote-text').remove();

                        // add footnote to materials list
                        const $footnote = $('<p>', {
                            class: 'js-product-disclaimer-footnote-text product-disclaimer-footnote-text',
                            text: disclaimer.footnote_str,
                        });
                        $footnote.append(' ');
                        $('<a>', {
                            href: disclaimer.url_href,
                            target: '_blank',
                            text: disclaimer.url_text,
                        }).appendTo($footnote);
                        $footnote.appendTo($(materialListSelector));
                    }
                });
            }
        }
    },
};

/**
 * Renders disclaimers for a single product.
 *
 * @param {Object} productData
 */
export function renderProductDisclaimers(productData) {
    Disclaimers.render({ [productData.id]: productData });
}

export default Disclaimers;
